import PropTypes from "prop-types"
import React, { useContext, useMemo, useRef, useState } from "react"
import { useMarina } from "src/contexts/MarinaContext"
import InvoiceDetail from "src/main/Billing/InvoiceDetail"
import PaymentDetail from "src/main/Billing/PaymentDetail"

import SideSheet from "src/components/SideSheet"

import { useQueryParamState } from "src/hooks/use_query_param_state"

import { BillingSheetsContext } from "./context"

const SHEETS = {
  invoice: {
    title: "Invoice",
    path: (marinaSlug, id) => `/manage/${marinaSlug}/invoices/${id}`,
    render: (id) => <InvoiceDetail invoiceId={id} />,
  },
  payment: {
    title: "Payment",
    path: (marinaSlug, id) => `/manage/${marinaSlug}/payment_txns/${id}`,
    render: (id) => <PaymentDetail paymentTxnId={id} />,
  },
}

const SHEET_ID = /^[A-Za-z0-9_-]+$/

// ?sheets=payment:<id>,invoice:<id> lists the open sheets bottom to top. Each
// sheet opens once, and only for an id that can't reshape the paths it fills.
const parseStack = (value) =>
  value
    .split(",")
    .map((token) => {
      const [kind, id] = token.split(":")
      return { kind, id, key: `${kind}:${id}` }
    })
    .filter(({ kind, id }) => SHEETS[kind] && SHEET_ID.test(id ?? ""))
    .filter(
      (sheet, index, sheets) =>
        sheets.findIndex(({ key }) => key === sheet.key) === index
    )

const serializeStack = (stack) => stack.map(({ key }) => key).join(",")

// Every sheet stays rendered, in the order it first opened, until Silk reports
// it has slid out: a sheet that leaves the stack is passed open={false} so
// Silk animates it away. The list is updated during the render that changes
// the stack, so a closing sheet is never unmounted, even for one render.
const useRenderedSheets = (stack) => {
  const stackKey = serializeStack(stack)
  const [rendered, setRendered] = useState(stack)
  const [renderedFor, setRenderedFor] = useState(stackKey)

  if (renderedFor !== stackKey) {
    setRenderedFor(stackKey)
    setRendered((current) => [
      ...current,
      ...stack.filter(({ key }) => !current.some((s) => s.key === key)),
    ])
  }

  const finishClosing = (key) =>
    setRendered((current) => current.filter((sheet) => sheet.key !== key))

  return [rendered, finishClosing]
}

// The open sheets live in the URL, so a refresh or a shared link reopens them,
// and useQueryParamState replaces the history entry, so the back button
// doesn't step through sheets. Opening a sheet already in the stack pops back
// to it; anything else goes on top. Silk orders, shifts and dismisses them.
const BillingSheetsHost = ({ children }) => {
  const { marinaSlug } = useMarina()
  const [param, setParam] = useQueryParamState("sheets", { defaultValue: "" })
  const stack = parseStack(param)
  const [rendered, finishClosing] = useRenderedSheets(stack)

  // Links read the context on every render, so its value stays the same object
  // and opening reads the stack as it is when the link is clicked.
  const latest = useRef()
  latest.current = { stack, setParam }

  const sheets = useMemo(() => {
    const open = (kind, id) => {
      const { stack: current, setParam: set } = latest.current
      const key = `${kind}:${id}`
      const index = current.findIndex((sheet) => sheet.key === key)
      set(
        serializeStack(
          index === -1
            ? [...current, { kind, id, key }]
            : current.slice(0, index + 1)
        )
      )
    }

    return {
      openInvoice: (id) => open("invoice", id),
      openPayment: (id) => open("payment", id),
    }
  }, [])

  const closeFrom = (key) => {
    const index = stack.findIndex((sheet) => sheet.key === key)
    if (index !== -1) setParam(serializeStack(stack.slice(0, index)))
  }

  return (
    <BillingSheetsContext.Provider value={sheets}>
      {children}
      <SideSheet.Stack>
        {rendered.map(({ kind, id, key }) => (
          <SideSheet.Root
            key={key}
            open={stack.some((sheet) => sheet.key === key)}
            onOpenChange={(next) => !next && closeFrom(key)}
          >
            <SideSheet.Content
              title={SHEETS[kind].title}
              fullPageHref={SHEETS[kind].path(marinaSlug, id)}
              onClosed={() => finishClosing(key)}
            >
              {SHEETS[kind].render(id)}
            </SideSheet.Content>
          </SideSheet.Root>
        ))}
      </SideSheet.Stack>
    </BillingSheetsContext.Provider>
  )
}

BillingSheetsHost.propTypes = {
  children: PropTypes.node.isRequired,
}

// One host per page: a screen rendered inside a sheet reuses the outer one.
const BillingSheets = ({ children }) => {
  const outer = useContext(BillingSheetsContext)

  return outer ? children : <BillingSheetsHost>{children}</BillingSheetsHost>
}

BillingSheets.propTypes = {
  children: PropTypes.node.isRequired,
}

export const withBillingSheets = (Component) => {
  const WithBillingSheets = (props) => (
    <BillingSheets>
      <Component {...props} />
    </BillingSheets>
  )
  WithBillingSheets.displayName = `withBillingSheets(${
    Component.displayName || Component.name
  })`
  return WithBillingSheets
}

export default BillingSheets
